import { ChangeDetectorRef, Component, Injector, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { DataService } from '../../../core/service/base/data.service';
import { FormGroup, FormBuilder, UntypedFormGroup } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ActivatedRoute } from '@angular/router';
import { Command, standardCommand } from '../../../core/model/command';
import { ExecutorGroup } from '../../model/executor-group';
import { ExecutorGroupService } from '../../service/data/executor-group.service';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { User } from '../../model/user';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { Subscription, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { SelectDialogComponent } from '../select-dialog/select-dialog.component';
import { UserService } from '../../service/data/user.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ExecutorGroupUser } from '../../model/executor-group-user';

@Component({
    selector: 'app-executor-group-form',
    templateUrl: './executor-group-form.component.html',
    styleUrls: ['./executor-group-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: ExecutorGroupService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExecutorGroupFormComponent extends FormComponentBase<ExecutorGroup> implements OnDestroy {
  itemListColumns: ColumnDefinition[];
  menuDefinition: MenuItemDefinition[];
  itemsList: User[] = [];
  users: User[];

  private _dialogSubscription = new Subscription();
  private isDeletingUser = false;
  private subscriptions: Subscription[] = [];
  private _isExit: boolean = false;

  constructor(
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    private _dataService: ExecutorGroupService,
    private _actionWindow: ActionWindowService,
    private _injector: Injector,
    private _userService: UserService,
    private cdr: ChangeDetectorRef,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    columnFactory: ColumnDefinitionFactory
  ) {
    super(activatedRoute, _dataService, messageService, navigation, dispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
    this.itemListColumns = [
      columnFactory.create('USERS_BTN', 'personName'),
      columnFactory.create('COMMON.EMAIL', 'name'),
      columnFactory.create('CIVICNUMBER', 'civicNumber'),
      columnFactory.create('USER.PHONENUMBER', 'phoneNumber'),
    ];
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this._dialogSubscription) this._dialogSubscription.unsubscribe();

    this.subscriptions.forEach(subscription => subscription.unsubscribe());

    this._isExit = true;
  }

  protected createModel(): ExecutorGroup {
    return new ExecutorGroup();
  }

  protected init(data: ExecutorGroup) {
    this._isExit = false;

    let subscription: Subscription;
    if (data.id) {
      subscription = forkJoin({
        users: this._userService.getList(),
        webUsersExecutorGroup: this._dataService.getWebUsersForExecutorGroup(data.id),
      }).subscribe(results => {
        this.users = results.users;
        this.itemsList = results.webUsersExecutorGroup.map(x => x.webUser);
        super.init(data);
      });
    } else {
      subscription = this._userService.getList().subscribe(result => {
        this.users = result;
        super.init(data);
      });
    }
    this.subscriptions.push(subscription);
  }

  protected getFormGroup(): FormGroup {
    const group = new UntypedFormGroup({
      id: this._fcf.createHidden(this.dataContext.id),
      name: this._fcf.createMandatory('EXECUTORGROUP.NAME', this.dataContext.name),
    });
    return group;
  }

  protected prepareSave(): ExecutorGroup {
    const model = super.prepareSave();
    return model;
  }

  itemClicked(e: { context: User; event: any }) {
    this.navigation.navigate(['users', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  execute(command: Command): void {
    switch (command.id) {
      case standardCommand.delete:
        if (this.isDeletingUser || this._isExit) break;

        const userToDelete = this.itemsList.filter(x => x.id === command.context.id);
        if (userToDelete && userToDelete.length === 1) {
          this.isDeletingUser = true;
          const subscription = this._dataService.deleteWebUsersFromExecutorGroup(userToDelete[0].id).subscribe({
            next: () => {
              this.itemsList = this.itemsList.filter(x => x.id !== command.context.id);
              this.isDeletingUser = false;
            },
            error: () => {
              this.isDeletingUser = false;
            },
          });
          this.subscriptions.push(subscription);
        } else {
          if (this.dataContext?.id) {
            const subscription = this._dataService.delete(this.dataContext?.id).subscribe(() => {
              this.subscriptions.push(subscription);
              this.navigation.navigate(['executor-groups']);
            });
          }
        }
        break;
    }
  }

  addUser() {
    if (this._dialogSubscription) {
      this._dialogSubscription.unsubscribe();
    }

    if (!this.dataContext.id) {
      const model = this.prepareSave();
      this._dataService.add(model).subscribe(id => {
        this.dataContext.id = id;
        this.saveUser();
      });
    } else {
      this.saveUser();
    }
  }

  get isAddUserDisabled(): boolean {
    var formControl = this.form.controls['name'];
    return this.getUserSelection().length === 0 || formControl.invalid;
  }

  private getUserSelection(): User[] {
    if (!this.itemsList && this.itemsList.length === 0) {
      return this.users;
    }

    const userSelection = this.users.filter(user => !this.itemsList.some(item => item.id === user.id));
    return userSelection;
  }

  private saveUser(): void {
    const users = this.getUserSelection()
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(c => new SelectionItem(c.displayName, c.id));

    this._dialogSubscription = this._actionWindow
      .open(SelectDialogComponent, this._injector, null, {
        itemsSource: users,
        fieldName: 'EXECUTORGROUP.USER',
        title: 'EXECUTORGROUP.SELECT_USER',
        defaultValue: users[0].value,
      })
      .pipe(
        switchMap(res => {
          const executorGroupUser = new ExecutorGroupUser();
          executorGroupUser.webUserId = res.value;
          executorGroupUser.executorGroupId = this.dataContext.id;
          return this._dataService.addWebUsersToExecutorGroup(executorGroupUser).pipe(
            map(() => res.value) // map to the user id
          );
        }),
        catchError(error => {
          console.error(error);
          return of(null);
        })
      )
      .subscribe(userId => {
        if (userId) {
          const user = this.users.find(x => x.id === userId);
          this.itemsList.push(user);
          this.cdr.detectChanges();
          this.form.markAsPristine();
        }
      });
  }
}
